import React, { useContext, useMemo } from 'react'
import {
  getServerDrilldownID,
  getApplicationDrilldownID
} from '../../../components/route'
import { RouteContext } from '../../../components/route-provider'
import { GridType, LIBRARIES_TABS } from '../helpers/ui-interfaces'
import { AssessGrid } from '../components/assess-grid'
import { getParamValue } from '../helpers/object-transforms'
import { CliGrid } from '../components/cli-grid'
import { useActiveGrid } from '../components/hooks/useActiveGrid'
import { HashRouter } from 'react-router-dom'
import { ActiveGridContextType } from '../components/filters/active-grid-provider'

export interface LibrariesPageContextValue {
  gridType: GridType
  applicationID?: string
  serverID?: string
  activeGrid: ActiveGridContextType['activeGrid']
  isStaticGrid: boolean
  updateActiveGrid: ActiveGridContextType['setActiveGrid']
  selectedQuickFilter: string
  setSelectedQuickFilter: (string) => void
  shouldShowCLIGrid: boolean
  activeRow?: string
  filterText?: string
  showPanel?: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const defaultLibrariesPageContext = {
  gridType: 'Organization' as GridType,
  activeGrid: LIBRARIES_TABS.RUNTIME,
  isStaticGrid: false,
  updateActiveGrid: noop,
  selectedQuickFilter: 'VULNERABLE',
  setSelectedQuickFilter: noop,
  shouldShowCLIGrid: false
}
export const LibrariesPageContext =
  React.createContext<LibrariesPageContextValue>(defaultLibrariesPageContext)
interface LibrariesPageProviderProps {
  gridType: GridType
}

/**
 * Retrieves data from the RouteContext and passes it to the Grid component
 */
export const LibrariesPageProvider = ({
  gridType
}: LibrariesPageProviderProps) => {
  let serverID: string | undefined
  let applicationID: string | undefined

  const { parameters, queryParameters, organizationID, setQueryParameter } =
    useContext(RouteContext)

  if (!organizationID) {
    throw new Error(
      'Libraries grid failed to load because organization ID could not be determined.'
    )
  }

  if (gridType === 'Server') {
    serverID = getServerDrilldownID(parameters)

    if (!serverID) {
      throw new Error('Unable to parse server ID from route')
    }
  }

  if (gridType === 'Application') {
    applicationID = getApplicationDrilldownID(parameters)

    if (!applicationID) {
      throw new Error(
        'Application libraries grid failed to load because application ID could not be determined.'
      )
    }
  }

  const filterText = useMemo(
    () => getParamValue('filterText', queryParameters) as string,
    [queryParameters]
  )
  const showPanel = useMemo(
    () => getParamValue('showPanel', queryParameters) as string,
    [queryParameters]
  )

  const activeRow = useMemo(
    () => getParamValue('activeRow', queryParameters) as string,
    [queryParameters]
  )

  const selectedQuickFilter = useMemo(
    () =>
      (getParamValue('quickFilter', queryParameters) as string) || 'VULNERABLE',
    [queryParameters]
  )

  const { activeGrid, isStaticGrid, updateActiveGrid, shouldShowCLIGrid } =
    useActiveGrid({
      gridType,
      applicationID
    })

  const setSelectedQuickFilter = (newQuickFilter: string) =>
    setQueryParameter('libraries', 'quickFilter', newQuickFilter)

  const librariesPageContext = useMemo(
    () => ({
      applicationID,
      gridType,
      serverID,
      activeGrid,
      isStaticGrid,
      updateActiveGrid,
      shouldShowCLIGrid,
      activeRow,
      showPanel,
      filterText,
      selectedQuickFilter,
      setSelectedQuickFilter
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applicationID,
      gridType,
      serverID,
      activeGrid,
      updateActiveGrid,
      isStaticGrid,
      shouldShowCLIGrid,
      activeRow,
      showPanel,
      filterText
    ]
  )

  return (
    <LibrariesPageContext.Provider value={librariesPageContext}>
      <LibrariesPage />
    </LibrariesPageContext.Provider>
  )
}

export const useLibrariesPageContext = () => useContext(LibrariesPageContext)

/**
 *
 * Determines whether to show the AssessGrid or CLIGrid
 */

const LibrariesPage = () => {
  const {
    organizationID,
    queryParameters,
    setQueryParameters,
    clearAll,
    clear,
    updateQueryParameters
  } = useContext(RouteContext)
  const {
    gridType,
    applicationID,
    serverID,
    activeGrid,
    updateActiveGrid,
    activeRow,
    showPanel,
    filterText,
    shouldShowCLIGrid
  } = useLibrariesPageContext()

  const renderCliGrid = useMemo(() => {
    return activeGrid === LIBRARIES_TABS.STATIC && shouldShowCLIGrid
  }, [activeGrid, shouldShowCLIGrid])

  return (
    <HashRouter>
      {renderCliGrid ? (
        <CliGrid
          clear={clear}
          // @ts-ignore I will always have an organization
          organizationID={organizationID}
          queryParameters={queryParameters}
          setQueryParameters={setQueryParameters}
          clearAll={clearAll}
          updateQueryParameters={updateQueryParameters}
          activeGrid={activeGrid}
          updateActiveGrid={updateActiveGrid}
          activeRow={activeRow}
          showPanel={showPanel}
          filterText={filterText}
        />
      ) : (
        <AssessGrid
          // @ts-ignore I will always have an organization
          organizationID={organizationID}
          clear={clear}
          applicationID={applicationID}
          serverID={serverID}
          gridType={gridType}
          queryParameters={queryParameters}
          setQueryParameters={setQueryParameters}
          clearAll={clearAll}
          updateQueryParameters={updateQueryParameters}
          activeGrid={activeGrid}
          updateActiveGrid={updateActiveGrid}
          filterText={filterText}
          activeRow={activeRow}
          showPanel={showPanel}
        />
      )}
    </HashRouter>
  )
}
